/**
 * Google Places Service
 * 
 * Integration with Google Places API for finding nearby businesses.
 * Used for competitor analysis in regional radius.
 */

import type { PlaceResult, PlaceDetails } from '../types/competitor';

const GOOGLE_PLACES_API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY || '';

/**
 * Calculate distance between two coordinates in kilometers
 */
function calculateDistance(
  lat1: number, 
  lng1: number, 
  lat2: number, 
  lng2: number
): number {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export const placesService = {
  /**
   * Check if Google Places API is configured
   */
  isConfigured(): boolean {
    return !!GOOGLE_PLACES_API_KEY;
  },

  /**
   * Geocode an address to coordinates
   */
  async geocodeAddress(address: string): Promise<{ lat: number; lng: number } | null> {
    if (!GOOGLE_PLACES_API_KEY) {
      console.error('Google Places API key not configured');
      return null;
    }

    try {
      console.log('📍 Geocoding address:', address);
      
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${GOOGLE_PLACES_API_KEY}`
      );
      
      if (!response.ok) {
        throw new Error(`Geocoding failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.status !== 'OK' || !data.results?.[0]) {
        console.warn('Geocoding returned no results:', data.status);
        return null;
      }
      
      const location = data.results[0].geometry.location;
      console.log('✅ Geocoded to:', location);
      
      return {
        lat: location.lat,
        lng: location.lng
      };
      
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  },

  /**
   * Find car dealerships nearby using Google Places API
   */
  async findDealershipsNearby(
    lat: number,
    lng: number,
    radiusKm: number = 40
  ): Promise<PlaceResult[]> {
    if (!GOOGLE_PLACES_API_KEY) {
      console.error('Google Places API key not configured');
      return [];
    }

    try {
      console.log(`🔍 Finding dealerships within ${radiusKm}km of ${lat}, ${lng}`);
      
      const radiusMeters = radiusKm * 1000;
      const results: PlaceResult[] = [];
      
      // Search for car dealerships
      const searchTypes = ['car_dealer'];
      const searchKeywords = ['autohaus', 'autohändler', 'car dealer'];
      
      for (const keyword of searchKeywords) {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/place/nearbysearch/json?` +
          `location=${lat},${lng}` +
          `&radius=${radiusMeters}` +
          `&keyword=${encodeURIComponent(keyword)}` +
          `&key=${GOOGLE_PLACES_API_KEY}`
        );
        
        if (!response.ok) {
          console.warn(`Places search failed for "${keyword}": ${response.status}`);
          continue;
        }
        
        const data = await response.json();
        
        if (data.status === 'OK' && data.results) {
          for (const place of data.results) {
            // Check if already in results
            if (results.some(r => r.placeId === place.place_id)) {
              continue;
            }
            
            const placeLat = place.geometry?.location?.lat;
            const placeLng = place.geometry?.location?.lng;
            
            if (!placeLat || !placeLng) continue;
            
            const distance = calculateDistance(lat, lng, placeLat, placeLng);
            
            // Only include if within radius
            if (distance <= radiusKm) {
              results.push({
                placeId: place.place_id,
                name: place.name,
                address: place.vicinity || place.formatted_address || '',
                location: { lat: placeLat, lng: placeLng },
                distanceKm: Math.round(distance * 10) / 10,
                rating: place.rating,
                reviewCount: place.user_ratings_total,
                openNow: place.opening_hours?.open_now,
                types: place.types,
                photos: place.photos?.map((p: any) => 
                  `https://maps.googleapis.com/maps/api/place/photo?maxwidth=400&photoreference=${p.photo_reference}&key=${GOOGLE_PLACES_API_KEY}`
                )
              });
            }
          }
        }
      }
      
      // Sort by distance
      results.sort((a, b) => a.distanceKm - b.distanceKm);
      
      console.log(`✅ Found ${results.length} dealerships within ${radiusKm}km`);
      
      return results;
      
    } catch (error) {
      console.error('Places search error:', error);
      return [];
    }
  },

  /**
   * Nearby search with arbitrary keywords (non-automotive competitors, same lat/lng radius).
   * Deduplicates by place_id; sorts by distance from (lat, lng).
   */
  async findNearbyBusinessesByKeywords(
    lat: number,
    lng: number,
    radiusKm: number,
    keywords: string[]
  ): Promise<PlaceResult[]> {
    if (!GOOGLE_PLACES_API_KEY) {
      console.error('Google Places API key not configured');
      return [];
    }

    const cleaned = [...new Set(keywords.map(k => k.trim()).filter(k => k.length >= 2))].slice(0, 8);
    if (cleaned.length === 0) return [];

    const radiusMeters = radiusKm * 1000;
    const results: PlaceResult[] = [];
    const seenPlaceIds = new Set<string>();

    try {
      for (const keyword of cleaned) {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/place/nearbysearch/json?` +
            `location=${lat},${lng}` +
            `&radius=${radiusMeters}` +
            `&keyword=${encodeURIComponent(keyword)}` +
            `&key=${GOOGLE_PLACES_API_KEY}`
        );

        if (!response.ok) {
          console.warn(`Places nearby search failed for "${keyword}": ${response.status}`);
          continue;
        }

        const data = await response.json();

        if (data.status === 'REQUEST_DENIED' && data.error_message) {
          console.error('Places REQUEST_DENIED:', data.error_message);
        }

        if (data.status !== 'OK' || !data.results) {
          continue;
        }

        for (const place of data.results) {
          if (!place.place_id || seenPlaceIds.has(place.place_id)) continue;
          seenPlaceIds.add(place.place_id);

          const placeLat = place.geometry?.location?.lat;
          const placeLng = place.geometry?.location?.lng;
          if (placeLat == null || placeLng == null) continue;

          const distance = calculateDistance(lat, lng, placeLat, placeLng);
          if (distance > radiusKm) continue;

          results.push({
            placeId: place.place_id,
            name: place.name,
            address: place.vicinity || place.formatted_address || '',
            location: { lat: placeLat, lng: placeLng },
            distanceKm: Math.round(distance * 10) / 10,
            rating: place.rating,
            reviewCount: place.user_ratings_total,
            openNow: place.opening_hours?.open_now,
            types: place.types,
            photos: place.photos?.map(
              (p: { photo_reference: string }) =>
                `https://maps.googleapis.com/maps/api/place/photo?maxwidth=400&photoreference=${p.photo_reference}&key=${GOOGLE_PLACES_API_KEY}`
            )
          });
        }

        if (cleaned.length > 4) {
          await new Promise(r => setTimeout(r, 150));
        }
      }

      results.sort((a, b) => a.distanceKm - b.distanceKm);
      console.log(`✅ Found ${results.length} places (keyword search) within ${radiusKm}km`);
      return results;
    } catch (error) {
      console.error('findNearbyBusinessesByKeywords error:', error);
      return [];
    }
  },

  /**
   * Get detailed information about a place
   */
  async getPlaceDetails(placeId: string): Promise<PlaceDetails | null> {
    if (!GOOGLE_PLACES_API_KEY) {
      console.error('Google Places API key not configured');
      return null;
    }

    try {
      console.log('📋 Getting details for place:', placeId);
      
      const fields = [
        'name',
        'formatted_address',
        'formatted_phone_number',
        'website',
        'rating',
        'user_ratings_total',
        'opening_hours',
        'reviews',
        'photos',
        'geometry',
        'price_level',
        'types'
      ].join(',');
      
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/place/details/json?` +
        `place_id=${placeId}` +
        `&fields=${fields}` +
        `&key=${GOOGLE_PLACES_API_KEY}`
      );
      
      if (!response.ok) {
        throw new Error(`Place details failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.status !== 'OK' || !data.result) {
        console.warn('Place details returned no results:', data.status);
        return null;
      }
      
      const place = data.result;
      
      return {
        placeId,
        name: place.name,
        address: place.vicinity || place.formatted_address || '',
        formattedAddress: place.formatted_address,
        location: {
          lat: place.geometry?.location?.lat || 0,
          lng: place.geometry?.location?.lng || 0
        },
        distanceKm: 0, // Will be calculated by caller if needed
        rating: place.rating,
        reviewCount: place.user_ratings_total,
        website: place.website,
        phone: place.formatted_phone_number,
        formattedPhone: place.formatted_phone_number,
        openNow: place.opening_hours?.open_now,
        openingHours: place.opening_hours ? {
          weekdayText: place.opening_hours.weekday_text || [],
          isOpen: place.opening_hours.open_now || false
        } : undefined,
        reviews: place.reviews?.map((r: any) => ({
          authorName: r.author_name,
          rating: r.rating,
          text: r.text,
          time: r.time
        })),
        photos: place.photos?.slice(0, 5).map((p: any) =>
          `https://maps.googleapis.com/maps/api/place/photo?maxwidth=800&photoreference=${p.photo_reference}&key=${GOOGLE_PLACES_API_KEY}`
        ),
        priceLevel: place.price_level,
        types: place.types
      };
      
    } catch (error) {
      console.error('Place details error:', error);
      return null;
    }
  },

  /**
   * Search for places by text query
   */
  async searchPlaces(query: string, lat?: number, lng?: number): Promise<PlaceResult[]> {
    if (!GOOGLE_PLACES_API_KEY) {
      console.error('Google Places API key not configured');
      return [];
    }

    try {
      console.log('🔍 Searching places:', query);
      
      let url = `https://maps.googleapis.com/maps/api/place/textsearch/json?` +
        `query=${encodeURIComponent(query)}` +
        `&key=${GOOGLE_PLACES_API_KEY}`;
      
      if (lat && lng) {
        url += `&location=${lat},${lng}&radius=50000`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Text search failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.status !== 'OK' || !data.results) {
        return [];
      }
      
      return data.results.map((place: any) => ({
        placeId: place.place_id,
        name: place.name,
        address: place.formatted_address || '',
        location: {
          lat: place.geometry?.location?.lat || 0,
          lng: place.geometry?.location?.lng || 0
        },
        distanceKm: 0,
        rating: place.rating,
        reviewCount: place.user_ratings_total,
        types: place.types
      }));
      
    } catch (error) {
      console.error('Text search error:', error);
      return [];
    }
  },

  /**
   * Find a business by its website URL
   * Searches for the business name/domain and returns matching places
   */
  async findBusinessByWebsite(websiteUrl: string): Promise<PlaceResult | null> {
    if (!GOOGLE_PLACES_API_KEY) {
      console.error('Google Places API key not configured');
      return null;
    }

    try {
      // Extract domain for search
      let domain: string;
      try {
        const urlObj = new URL(websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`);
        domain = urlObj.hostname.replace('www.', '');
      } catch {
        domain = websiteUrl.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0];
      }

      console.log('🔍 Finding business by website:', domain);
      
      // Search for businesses matching the domain/business name
      const results = await this.searchPlaces(domain);
      
      if (results.length === 0) {
        // Try with just the company name (before .com/.de etc)
        const companyName = domain.split('.')[0];
        const altResults = await this.searchPlaces(companyName);
        
        if (altResults.length > 0) {
          // Get details to check website match
          for (const result of altResults.slice(0, 3)) {
            const details = await this.getPlaceDetails(result.placeId);
            if (details?.website) {
              const detailsDomain = this.extractDomain(details.website);
              if (detailsDomain === domain || domain.includes(detailsDomain) || detailsDomain.includes(domain.split('.')[0])) {
                return {
                  ...result,
                  website: details.website,
                  phone: details.phone
                };
              }
            }
          }
        }
        
        return null;
      }
      
      // Check first few results for matching website
      for (const result of results.slice(0, 5)) {
        const details = await this.getPlaceDetails(result.placeId);
        if (details?.website) {
          const detailsDomain = this.extractDomain(details.website);
          if (detailsDomain === domain || domain.includes(detailsDomain) || detailsDomain.includes(domain.split('.')[0])) {
            console.log('✅ Found matching business:', result.name);
            return {
              ...result,
              website: details.website,
              phone: details.phone
            };
          }
        }
      }
      
      // Fallback: return first result if no website match
      if (results.length > 0) {
        const details = await this.getPlaceDetails(results[0].placeId);
        return {
          ...results[0],
          website: details?.website,
          phone: details?.phone
        };
      }
      
      return null;
      
    } catch (error) {
      console.error('Find business by website error:', error);
      return null;
    }
  },

  /**
   * Helper: Extract domain from URL
   */
  extractDomain(url: string): string {
    try {
      const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
      return urlObj.hostname.replace('www.', '').toLowerCase();
    } catch {
      return url.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0].toLowerCase();
    }
  },

  // ============================================================================
  // DEALER LOCATION ENRICHMENT
  // ============================================================================

  /**
   * Enrich a dealer location with Google Places data
   * Returns enriched location data with coordinates, rating, reviews, etc.
   */
  async enrichDealerLocation(location: {
    name: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
  }): Promise<{
    coordinates?: { lat: number; lng: number };
    placeId?: string;
    googleRating?: number;
    googleReviewCount?: number;
    googleMapsUrl?: string;
    openingHours?: any;
    phone?: string;
    website?: string;
  } | null> {
    if (!GOOGLE_PLACES_API_KEY) {
      console.error('Google Places API key not configured');
      return null;
    }

    try {
      console.log('🏢 Enriching dealer location:', location.name);
      
      const result: {
        coordinates?: { lat: number; lng: number };
        placeId?: string;
        googleRating?: number;
        googleReviewCount?: number;
        googleMapsUrl?: string;
        openingHours?: any;
        phone?: string;
        website?: string;
      } = {};
      
      // 1. Geocode the full address
      const fullAddress = `${location.address}, ${location.postalCode} ${location.city}, ${location.country}`;
      const coords = await this.geocodeAddress(fullAddress);
      
      if (coords) {
        result.coordinates = coords;
      }
      
      // 2. Search for the business
      const searchQuery = `${location.name} ${location.city}`;
      const places = await this.searchPlaces(searchQuery);
      
      if (places.length > 0) {
        const topResult = places[0];
        result.placeId = topResult.placeId;
        
        // 3. Get detailed place info
        const details = await this.getPlaceDetails(topResult.placeId);
        
        if (details) {
          result.googleRating = details.rating;
          result.googleReviewCount = details.userRatingsTotal;
          result.googleMapsUrl = details.url;
          result.phone = details.phone;
          result.website = details.website;
          
          if (details.openingHours) {
            result.openingHours = details.openingHours;
          }
        }
      }
      
      console.log('✅ Location enriched:', {
        hasCoords: !!result.coordinates,
        hasPlaceId: !!result.placeId,
        rating: result.googleRating,
        reviews: result.googleReviewCount
      });
      
      return result;
      
    } catch (error) {
      console.error('Error enriching dealer location:', error);
      return null;
    }
  },

  /**
   * Get Google Maps URL for a location
   */
  getGoogleMapsUrl(location: { lat: number; lng: number } | string): string {
    if (typeof location === 'string') {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`;
  },

  /**
   * Get Google Maps directions URL
   */
  getDirectionsUrl(destination: { lat: number; lng: number } | string): string {
    if (typeof destination === 'string') {
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
    }
    return `https://www.google.com/maps/dir/?api=1&destination=${destination.lat},${destination.lng}`;
  }
};
